import apiClient from './api';
import type { Category, CategoryRequirement, RaceEvent, GlobalDocument, GlobalDocumentType } from '../types';

/**
 * Service module for admin API operations.
 *
 * Requirement 15.2 — Race event creation with name, year, and category.
 * Requirement 15.3 — Upload race-specific documents (Notice of Race, Sailing Instructions).
 * Requirement 15.4 — Upload global documents (worldwide rules, regional requirements).
 * Requirement 15.5 — Parse category requirements documents.
 * Requirement 15.10 — Maintain version history for global documents.
 */

export interface CreateRaceEventData {
  name: string;
  year: number;
  category: string;
  date?: string;  // Event date in YYYY-MM-DD format
}

export interface UpdateRaceEventData {
  name?: string;
  year?: number;
  category?: string;
  date?: string;  // Event date in YYYY-MM-DD format
}

export interface WhatsAppSettings {
  enabled: boolean;
  phoneNumberId: string;
  accessToken: string;  // masked, last 4 chars only from GET
  verifyToken: string;
}

export interface WhatsAppSettingsUpdate {
  enabled: boolean;
  phoneNumberId: string;
  accessToken: string;
  verifyToken: string;
}

export type RaceDocumentType = 'notice_of_race' | 'sailing_instructions';

export const adminService = {
  /** Fetch all parsed categories for race event creation. */
  getCategories: async (): Promise<Category[]> => {
    const response = await apiClient.get<{ categories: Category[] }>('/admin/categories');
    return response.data.categories || [];
  },

  /** Create a new race event. */
  createRaceEvent: async (data: CreateRaceEventData): Promise<RaceEvent> => {
    const response = await apiClient.post<RaceEvent>('/admin/races', data);
    return response.data;
  },

  /** Update an existing race event. */
  updateRaceEvent: async (raceId: string, data: UpdateRaceEventData): Promise<RaceEvent> => {
    const response = await apiClient.put<RaceEvent>(`/admin/races/${raceId}`, data);
    return response.data;
  },

  /** Delete a race event. */
  deleteRaceEvent: async (raceId: string): Promise<void> => {
    await apiClient.delete(`/admin/races/${raceId}`);
  },

  /** Upload a race-specific document using presigned URL. */
  uploadRaceDocument: async (raceId: string, file: File, docType: RaceDocumentType): Promise<void> => {
    // Step 1: Request presigned URL from backend
    const urlResponse = await apiClient.post<{
      upload_url: string;
      document_id: string;
    }>(`/admin/documents/race/${raceId}/upload-url`, {
      type: docType,
      file_name: file.name,
    });

    const { upload_url, document_id } = urlResponse.data;

    // Step 2: Upload file directly to S3 using presigned URL
    const uploadResponse = await fetch(upload_url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'application/pdf',
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
    }

    // Step 3: Confirm upload completion with backend
    await apiClient.post(`/admin/documents/race/${raceId}/confirm`, {
      document_id,
    });
  },

  /** Fetch all global documents. */
  getGlobalDocuments: async (): Promise<GlobalDocument[]> => {
    const response = await apiClient.get<GlobalDocument[]>('/admin/documents/global');
    return response.data;
  },

  /** Update category requirements (manual corrections by admin). */
  updateCategory: async (categoryId: string, requirements: CategoryRequirement[]): Promise<Category> => {
    const response = await apiClient.put<Category>(`/admin/categories/${categoryId}`, { requirements });
    return response.data;
  },

  /** Upload a global document using presigned URL (for large files). */
  uploadGlobalDocument: async (file: File, type: GlobalDocumentType): Promise<GlobalDocument> => {
    // Step 1: Request presigned URL from backend
    const urlResponse = await apiClient.post<{
      upload_url: string;
      document_id: string;
      s3_key: string;
      version: string;
      type: string;
    }>('/admin/documents/global/upload-url', {
      type,
      file_name: file.name,
    });

    const { upload_url, document_id } = urlResponse.data;

    // Step 2: Upload file directly to S3 using presigned URL
    const uploadResponse = await fetch(upload_url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'application/pdf',
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
    }

    // Step 3: Confirm upload completion with backend
    const confirmResponse = await apiClient.post<{ document: GlobalDocument }>(
      '/admin/documents/global/confirm',
      { document_id }
    );

    return confirmResponse.data.document;
  },

  /** Fetch current WhatsApp integration settings (access token masked). */
  getWhatsAppSettings: async (): Promise<WhatsAppSettings> => {
    const response = await apiClient.get<WhatsAppSettings>('/admin/settings/whatsapp');
    return response.data;
  },

  /** Update WhatsApp integration settings. */
  updateWhatsAppSettings: async (settings: WhatsAppSettingsUpdate): Promise<void> => {
    await apiClient.put('/admin/settings/whatsapp', settings);
  },
};
